import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

// Content Components
import { HeroSection } from "@/features/funnels/components/renderer/HeroSection";
import { FeaturesSection } from "@/features/funnels/components/renderer/FeaturesSection";
import { TestimonialsSection } from "@/features/funnels/components/renderer/TestimonialsSection";
import { ProductDetailsSection } from "@/features/funnels/components/renderer/ProductDetailsSection";
import { CheckoutFormSection } from "@/features/funnels/components/renderer/CheckoutFormSection";
import { UpsellSection } from "@/features/funnels/components/renderer/UpsellSection";
import { ThankYouSection } from "@/features/funnels/components/renderer/ThankYouSection";
import { VideoSection } from "@/features/funnels/components/renderer/VideoSection";
import { FAQSection } from "@/features/funnels/components/renderer/FAQSection";
import { TrustBadges } from "@/features/funnels/components/renderer/TrustBadges";
import { TextBlock } from "@/features/funnels/components/renderer/TextBlock";
import { AnnouncementBar } from "@/features/funnels/components/renderer/AnnouncementBar";
import { CountdownTimer } from "@/features/funnels/components/renderer/CountdownTimer";
import { ProgressBar } from "@/features/funnels/components/renderer/ProgressBar";

// Builder Components
import { ImageCarouselPreview } from "@/components/landing-builder/ImageCarouselPreview";
import { ReviewsCarouselPreview } from "@/components/landing-builder/ReviewsCarouselPreview";
import { ProblemsSolutionsPreview } from "@/components/landing-builder/ProblemsSolutionsPreview";
import { FloatingCTAPreview } from "@/components/landing-builder/FloatingCTAPreview";
import { AdvancedFormPreview } from "@/components/landing-builder/AdvancedFormPreview";
import { FormPreview } from "@/components/landing-builder/FormPreview";
import { ProductStatsPreview } from "@/components/landing-builder/ProductStatsPreview";
import { PriceComparisonPreview } from "@/components/landing-builder/PriceComparisonPreview";
import { StockBarPreview } from "@/components/landing-builder/StockBarPreview";
import { BundlePopupPreview } from "@/components/landing-builder/BundlePopupPreview";
import { PricingTablePreview } from "@/components/landing-builder/PricingTablePreview";
import { BeforeAfterSliderPreview } from "@/components/landing-builder/BeforeAfterSliderPreview";
import { LiveNotificationsPreview } from "@/components/landing-builder/LiveNotificationsPreview";
import { StatsChartPreview } from "@/components/landing-builder/StatsChartPreview";
import { VideoAudioPreview } from "@/components/landing-builder/VideoAudioPreview";
import { BundleGiftPreview } from "@/components/landing-builder/BundleGiftPreview";
import { BundlePreview } from "@/components/landing-builder/BundlePreview";
import { ProgressBarPreview } from "@/components/landing-builder/ProgressBarPreview";
import { TabsView } from "@/features/funnels/components/renderer/TabsView";
import { AccordionView } from "@/features/funnels/components/renderer/AccordionView";
import { MultiColumnView } from "@/features/funnels/components/renderer/MultiColumnView";
import { LiveAddToCart, LiveCheckoutForm } from "@/features/funnels/components/renderer/LiveFunnelComponents";

import { ImageIcon, Star } from "lucide-react";
import {
    StaticButtonPreview,
    WhatsAppButtonPreview
} from "@/components/landing-builder/previews/ButtonPreviews";
import {
    ProductTitlePreview,
    ProductPricePreview,
    ProductImagePreview,
    ProductRatingPreview,
    AddToCartPreview,
    ProductDescriptionPreview,
    ProductGalleryPreview,
    ProductVariantsPreview
} from "@/components/landing-builder/previews/ProductPreviews";
import {
    CheckoutExpressPreview,
    CheckoutShippingPreview,
    CheckoutPaymentPreview,
    CheckoutSummaryPreview,
    OrderBumpPreview
} from "@/components/landing-builder/previews/CheckoutPreviews";
import {
    SocialIconsPreview,
    StickyBarPreview
} from "@/components/landing-builder/previews/SocialPreviews";

// Helper for recursive rendering
const renderElement = (element: any) => {
    const elementType = element.element_type || element.type;
    const BlockComponent = BLOCK_COMPONENTS[elementType];
    if (!BlockComponent) return null;
    return (
        <BlockComponent
            key={element.id}
            id={element.id}
            data={element.settings || {}}
            renderElement={renderElement}
        />
    );
};

// Block Component Mapping
const BLOCK_COMPONENTS: Record<string, any> = {
    // Legacy / Funnel Specific Blocks
    "hero_product": HeroSection,
    "features_section": (props: any) => <FeaturesSection features={props.data.features} />,
    "testimonials": TestimonialsSection,
    "checkout_form": CheckoutFormSection,
    "upsell_action": UpsellSection,
    "trust_badges": TrustBadges,
    "text_block": TextBlock,
    "announcement_bar": AnnouncementBar,
    "countdown_timer": CountdownTimer,
    "video": VideoSection,

    // Landing Builder Blocks
    "image": (props: any) => (
        <div style={{
            marginTop: `${props.data.marginTop || 0}px`,
            marginBottom: `${props.data.marginBottom || 0}px`,
            textAlign: 'center'
        }}>
            {props.data.imageUrl ? (
                <img
                    src={props.data.imageUrl}
                    alt={props.data.alt}
                    style={{
                        width: props.data.width || "100%",
                        maxWidth: props.data.maxWidth || "100%",
                        height: props.data.height || "auto",
                        objectFit: props.data.objectFit || "cover",
                        borderRadius: `${props.data.borderRadius || 0}px`,
                    }}
                    loading="lazy"
                />
            ) : (
                <div className="bg-muted p-8 text-center">
                    <ImageIcon className="mx-auto h-12 w-12 text-muted-foreground mb-2" />
                </div>
            )}
        </div>
    ),
    "text": (props: any) => (
        <div
            style={{
                backgroundColor: props.data.backgroundColor || "transparent",
                color: props.data.color || "#000000",
                textAlign: props.data.textAlign || "center",
                fontSize: `${props.data.fontSize || 16}px`,
                fontWeight: props.data.fontWeight || "400",
                lineHeight: props.data.lineHeight || "1.5",
                padding: `${props.data.padding || 16}px`,
                marginTop: `${props.data.marginTop || 0}px`,
                marginBottom: `${props.data.marginBottom || 0}px`,
            }}
        >
            <div dangerouslySetInnerHTML={{ __html: props.data.content }} />
        </div>
    ),
    "animated_text": (props: any) => (
        <div
            className={`
              ${props.data.animationType === "fade-in" ? "animate-fade-in" : ""}
              ${props.data.animationType === "slide-up" ? "animate-[slide-in-from-bottom_1s_ease-out]" : ""}
              ${props.data.animationType === "slide-down" ? "animate-[slide-in-from-top_1s_ease-out]" : ""}
              ${props.data.animationType === "slide-left" ? "animate-[slide-in-from-right_1s_ease-out]" : ""}
              ${props.data.animationType === "slide-right" ? "animate-[slide-in-from-left_1s_ease-out]" : ""}
              ${props.data.animationType === "zoom-in" ? "animate-scale-in" : ""}
              ${props.data.animationType === "bounce" ? "animate-[bounce_1s_ease-in-out]" : ""}
            `}
            style={{
                backgroundColor: props.data.backgroundColor || "transparent",
                color: props.data.color || "#10b981",
                textAlign: props.data.textAlign || "center",
                fontSize: `${props.data.fontSize || 32}px`,
                fontWeight: props.data.fontWeight || "700",
                padding: `${props.data.padding || 24}px`,
                marginTop: `${props.data.marginTop || 0}px`,
                marginBottom: `${props.data.marginBottom || 0}px`,
            }}
        >
            {props.data.content}
        </div>
    ),
    "static_button": (props: any) => <StaticButtonPreview settings={props.data} />,
    "whatsapp_button": (props: any) => <WhatsAppButtonPreview settings={props.data} />,
    "countdown": (props: any) => (
        <div className="p-6 text-center bg-muted">
            <h3 className="text-xl font-bold mb-4">{props.data.title}</h3>
            <div className="flex gap-4 justify-center">
                {props.data.showDays && <div className="bg-background p-4 rounded"><div className="text-2xl font-bold">00</div><div className="text-sm">يوم</div></div>}
                {props.data.showHours && <div className="bg-background p-4 rounded"><div className="text-2xl font-bold">00</div><div className="text-sm">ساعة</div></div>}
                {props.data.showMinutes && <div className="bg-background p-4 rounded"><div className="text-2xl font-bold">00</div><div className="text-sm">دقيقة</div></div>}
                {props.data.showSeconds && <div className="bg-background p-4 rounded"><div className="text-2xl font-bold">00</div><div className="text-sm">ثانية</div></div>}
            </div>
        </div>
    ),
    "progress_bar": (props: any) => <ProgressBarPreview settings={props.data} />,
    "faq": (props: any) => (
        <div className="p-6">
            <h3 className="text-2xl font-bold mb-6 text-center">{props.data.title}</h3>
            <div className="space-y-4">
                {props.data.items?.map((item: any, idx: number) => (
                    <div key={idx} className="border rounded-lg p-4">
                        <div className="font-bold mb-2">{item.question}</div>
                        <div className="text-sm text-muted-foreground">{item.answer}</div>
                    </div>
                ))}
            </div>
        </div>
    ),
    "reviews": (props: any) => (
        <div className="p-6">
            <h3 className="text-2xl font-bold mb-6 text-center">{props.data.title}</h3>
            <div className="grid gap-4">
                {props.data.reviews?.map((review: any, idx: number) => (
                    <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                                {review.avatar ? <img
                                    src={review.avatar}
                                    alt={review.name}
                                    className="w-12 h-12 rounded-full"
                                    loading="lazy"
                                    decoding="async"
                                /> : <span>{review.name[0]}</span>}
                            </div>
                            <div>
                                <div className="font-bold">{review.name}</div>
                                <div className="flex gap-1">
                                    {Array.from({ length: review.rating }).map((_, i) => (
                                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                                    ))}
                                </div>
                            </div>
                        </div>
                        <p className="text-sm">{review.comment}</p>
                    </div>
                ))}
            </div>
        </div>
    ),
    "announcement": (props: any) => (
        <div
            className="p-3 text-center overflow-hidden whitespace-nowrap"
            style={{
                backgroundColor: props.data.backgroundColor,
                color: props.data.textColor,
            }}
        >
            <div className="animate-marquee">{props.data.text}</div>
        </div>
    ),
    "header": () => null,
    "footer": (props: any) => (
        <div
            className="p-8 text-center"
            style={{
                backgroundColor: props.data.backgroundColor,
                color: props.data.textColor,
            }}
        >
            <p>{props.data.text}</p>
        </div>
    ),

    // New Builder Components
    "form": (props: any) => <FormPreview settings={props.data} />,
    "advanced_form": (props: any) => <AdvancedFormPreview settings={props.data} />,
    "image_carousel": (props: any) => <ImageCarouselPreview settings={props.data} />,
    "reviews_carousel": (props: any) => <ReviewsCarouselPreview settings={props.data} />,
    "problems_solutions": (props: any) => <ProblemsSolutionsPreview settings={props.data} />,
    "floating_cta": (props: any) => <FloatingCTAPreview settings={props.data} />,
    "product_stats": (props: any) => <ProductStatsPreview settings={props.data} />,
    "price_comparison": (props: any) => <PriceComparisonPreview settings={props.data} />,
    "stock_bar": (props: any) => <StockBarPreview settings={props.data} />,
    "bundle_popup": (props: any) => <BundlePopupPreview settings={props.data} />,
    "pricing_table": (props: any) => <PricingTablePreview settings={props.data} />,
    "before_after_slider": (props: any) => <BeforeAfterSliderPreview settings={props.data} />,
    "live_notifications": (props: any) => <LiveNotificationsPreview settings={props.data} />,
    "stats_chart": (props: any) => <StatsChartPreview settings={props.data} />,
    "video_audio": (props: any) => <VideoAudioPreview settings={props.data} />,
    "bundle_gift": (props: any) => <BundleGiftPreview settings={props.data} />,
    "bundle": (props: any) => <BundlePreview settings={props.data} />,

    // Layout Components (Using View versions)
    "two_columns": (props: any) => <MultiColumnView settings={props.data} columnsCount={2} renderElement={renderElement} />,
    "three_columns": (props: any) => <MultiColumnView settings={props.data} columnsCount={3} renderElement={renderElement} />,
    "four_columns": (props: any) => <MultiColumnView settings={props.data} columnsCount={4} renderElement={renderElement} />,
    "tabs": (props: any) => <TabsView id={props.id} data={props.data} renderElement={renderElement} />,
    "accordion": (props: any) => <AccordionView id={props.id} data={props.data} renderElement={renderElement} />,

    // Product Elements
    "product_title": (props: any) => <ProductTitlePreview settings={props.data} />,
    "product_price": (props: any) => <ProductPricePreview settings={props.data} />,
    "product_image": (props: any) => <ProductImagePreview settings={props.data} />,
    "product_rating": (props: any) => <ProductRatingPreview settings={props.data} />,
    "add_to_cart": (props: any) => <LiveAddToCart data={props.data} onNext={props.onNext} />,
    "product_description": (props: any) => <ProductDescriptionPreview settings={props.data} />,
    "product_gallery": (props: any) => <ProductGalleryPreview settings={props.data} />,
    "product_variants": (props: any) => <ProductVariantsPreview settings={props.data} />,

    // Checkout Elements
    "checkout_express": (props: any) => <LiveCheckoutForm data={props.data} onNext={props.onNext} funnelId={props.funnelId} stepId={props.stepId} />,
    "checkout_shipping": (props: any) => <CheckoutShippingPreview settings={props.data} />,
    "checkout_payment": (props: any) => <CheckoutPaymentPreview settings={props.data} />,
    "checkout_summary": (props: any) => <CheckoutSummaryPreview settings={props.data} />,
    "order_bump": (props: any) => <OrderBumpPreview settings={props.data} />,

    // Social & Sticky Elements
    "social_icons": (props: any) => <SocialIconsPreview settings={props.data} />,
    "sticky_bar": (props: any) => <StickyBarPreview settings={props.data} />,
};

export default function FunnelRenderer({ funnelIdProp }: { funnelIdProp?: string }) {
    const params = useParams();
    const navigate = useNavigate();

    // Use prop if available (custom domain), otherwise URL param
    const funnelId = funnelIdProp || params.funnelId;
    const stepSlug = funnelIdProp ? window.location.pathname.substring(1) : params.stepSlug;

    const [currentStep, setCurrentStep] = useState<any>(null);

    // 1. Fetch Funnel Data
    const { data: funnel, isLoading: funnelLoading } = useQuery({
        queryKey: ["public-funnel", funnelId],
        queryFn: async () => {
            const res = await fetch(`/api/funnels/${funnelId}`);
            const json = await res.json();
            if (json.error) throw new Error(json.error.message);
            return json.data;
        },
        enabled: !!funnelId
    });

    // 2. Determine Current Step
    useEffect(() => {
        if (funnel && funnel.steps) {
            let step;
            if (stepSlug) {
                step = funnel.steps.find((s: any) => s.id === stepSlug);
            } else {
                step = funnel.steps.find((s: any) => s.type === 'input') || funnel.steps[0];
            }

            if (step) {
                setCurrentStep(step);
                trackView(funnel.id, step.id);
                // Update Title
                document.title = step.content?.hero?.title || step.name || funnel.name;
            }
        }
    }, [funnel, stepSlug]);

    const trackView = async (funnelId: string, stepId: string) => {
        try {
            await fetch(`/api/analytics/track`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    funnel_id: funnelId,
                    step_id: stepId,
                    event_type: 'view',
                    session_id: localStorage.getItem('funnel_session_id') || `sess_${Date.now()}`
                })
            });
        } catch (e) {
            console.error("Tracking error", e);
        }
    };

    const handleNextStep = (nextStepId?: string) => {
        if (nextStepId) {
            navigate(`/f/${funnelId}/${nextStepId}`);
            return;
        }

        if (funnel && currentStep) {
            const connection = funnel.connections.find((c: any) => c.from_step_id === currentStep.id);
            if (connection) {
                navigate(`/f/${funnelId}/${connection.to_step_id}`);
            }
        }
    };

    if (funnelLoading) {
        return (
            <div className="h-screen w-full flex items-center justify-center bg-white">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!funnel || !currentStep) {
        return <div className="p-8 text-center">Funnel or Step not found</div>;
    }

    const content = currentStep.content || {};
    const pageSettings = content.pageSettings || {};

    return (
        <div
            className="min-h-screen font-cairo"
            dir="rtl"
            style={{
                backgroundColor: pageSettings.backgroundColor || "#ffffff",
                fontFamily: pageSettings.fontFamily || "Cairo",
            }}
        >
            {/* Dynamic Block Rendering */}
            {content.elements && Array.isArray(content.elements) ? (
                <div className={`flex flex-col w-full ${pageSettings.pageWidth === 'boxed' ? 'max-w-5xl mx-auto shadow-sm my-8' : ''}`}>
                    {content.elements.map((element: any, idx: number) => {
                        const elementType = element.element_type || element.type;
                        const BlockComponent = BLOCK_COMPONENTS[elementType];
                        if (!BlockComponent) return null;
                        return (
                            <BlockComponent
                                key={element.id || idx}
                                id={element.id}
                                data={element.settings || {}}
                                onNext={() => handleNextStep()}
                                funnelId={funnel.id}
                                stepId={currentStep.id}
                            />
                        );
                    })}
                </div>
            ) : (
                // Legacy / Template Rendering (Fixed Sections)
                <>
                    {content.hero && <HeroSection data={content.hero} onNext={() => handleNextStep()} />}
                    {content.productDetails && <ProductDetailsSection data={content.productDetails} />}
                    {content.videoUrl && <VideoSection data={{ videoUrl: content.videoUrl, title: content.title }} />}
                    {content.features && <FeaturesSection features={content.features} />}
                    {content.specifications && (
                        <div className="container mx-auto px-4 py-12">
                            <h3 className="text-2xl font-bold mb-6 text-center">المواصفات</h3>
                            <ul className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl mx-auto">
                                {content.specifications.map((spec: string, idx: number) => (
                                    <li key={idx} className="flex items-center gap-2 bg-gray-50 p-4 rounded-lg">
                                        <span className="w-2 h-2 bg-primary rounded-full" />
                                        {spec}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {content.testimonials && <TestimonialsSection testimonials={content.testimonials} />}
                    {content.reviews && <TestimonialsSection testimonials={content.reviews.reviews || content.reviews.featured} title="آراء العملاء" />}
                    {content.faqs && <FAQSection faqs={content.faqs} />}
                    {content.checkoutForm && <CheckoutFormSection data={content.checkoutForm} bundles={content.bundles} onSubmit={() => handleNextStep()} />}
                    {content.upsellOffer && <UpsellSection data={content.upsellOffer} onAccept={() => handleNextStep()} onDecline={() => handleNextStep()} />}
                    {content.thankYou && <ThankYouSection data={content.thankYou} />}
                </>
            )}

            {/* Fallback for empty content */}
            {(!content.elements || content.elements.length === 0) && Object.keys(content).length === 0 && (
                <div className="py-20 text-center text-gray-500">
                    <p>هذه الصفحة فارغة حالياً.</p>
                    <button onClick={() => handleNextStep()} className="mt-4 text-blue-600 underline">
                        تخطى للصفحة التالية (للمعاينة)
                    </button>
                </div>
            )}
        </div>
    );
}
